// Session-scoped record of INDEPENDENT Print Jobs — proofreading-only designs created from the
// product detail page that are deliberately never linked to a cart item (Addendum 2 §1.2 option 1).
// The record is what flips the product-detail button from "Create design" to "Edit design" and
// routes its click to the edit-or-new chooser instead of the initial start dialog (§1.3).
//
// Keyed per product (model code, see getIndependentPrintJobStoreKey in tron-designer.ts) so each
// product remembers its own design. sessionStorage gives exactly the required lifetime: cleared
// when the session ends, at which point the button naturally returns to "Create design".
const STORE_KEY = "td_independent_print_jobs";

type IndependentPrintJobMap = Record<string, string>; // product store key -> print job guid

function readMap(): IndependentPrintJobMap {
    try {
        const raw = window.sessionStorage.getItem(STORE_KEY);
        return raw ? JSON.parse(raw) as IndependentPrintJobMap : {};
    } catch {
        // sessionStorage unavailable (e.g. private mode) or corrupted value — behave as "no designs yet".
        return {};
    }
}

function writeMap(map: IndependentPrintJobMap): void {
    try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(map));
    } catch {
        // Best-effort: without storage the button simply keeps offering "Create design".
    }
}

export function getIndependentPrintJobGuid(productKey: string): string | null {
    return readMap()[productKey] ?? null;
}

export function setIndependentPrintJobGuid(productKey: string, printJobGuid: string): void {
    const map = readMap();
    map[productKey] = printJobGuid;
    writeMap(map);
}

export function clearIndependentPrintJob(productKey: string): void {
    const map = readMap();
    if (!(productKey in map)) {
        return;
    }
    delete map[productKey];
    writeMap(map);
}
